import React, { type FC, memo } from "react";
import clsx from "clsx";
import EcoLabel from "../../../../../shared-components/css-modules/lexus/molecules/EcoLabel";
import HybridLabel from "../../../../../shared-components/css-modules/lexus/molecules/HybridLabel";
import { useGradeSelectorSelector } from "../../../../redux/hooks";
import { type DisableEcoItemType, type EcoItem as EcoItemDataType, getEcoItems } from "../../../../utils/ecoUtils";
import { getGradeSelectorLabel } from "../../../../utils/labels";
import { useCommonOptionalLabel } from "../../../../../../../common-deprecated/utils/commonLabels";
import type { GradeBodyTypeEngineType } from "../../../../utils/types/Model";
import styles from "./Item.module.scss";
import EfficiencyClass from "./specs/EfficiencyClass";
import type { CommonSettingsType } from "../../../../../../../common-deprecated/settings/fetchCommonSettings";
import type { GradeSelectorSettingsType } from "../../../../redux/reducers/GradeSelectorSettingsReducer";

type EcoItemType = {
    ecoItem: EcoItemDataType;
    disable: DisableEcoItemType;
    commonSettings: CommonSettingsType;
    gradeSelectorSettings: GradeSelectorSettingsType;
};

const EcoItem: FC<EcoItemType> = ({ ecoItem, disable, commonSettings, gradeSelectorSettings }) => {
    const { ecoBadgeUrl, ecoTag, ecoTagLabel, efficiencyLabelConfig } = ecoItem;
    return (
        <div className={styles["eco-item"]}>
            {efficiencyLabelConfig.show && <EfficiencyClass label={efficiencyLabelConfig.label} />}
            {ecoBadgeUrl && <EcoLabel url={ecoBadgeUrl} />}
            {!disable?.ecoTag && (
                <HybridLabel
                    variant={ecoTagLabel ? ecoTag : "default"}
                    label={getGradeSelectorLabel(
                        { commonSettings, gradeSelectorSettings },
                        ecoTagLabel || "gradeSelectorFuelTypeStandard",
                    )}
                    hideBackground
                />
            )}
        </div>
    );
};

const EcoItems: FC<{
    limit: number;
    modelId: string;
    engines: GradeBodyTypeEngineType[];
    disable?: DisableEcoItemType;
    hasAdditionalIncentives: boolean;
}> = ({ limit = 3, modelId, engines, disable, hasAdditionalIncentives }) => {
    const gradeSelectorSettings = useGradeSelectorSelector((state) => state.gradeSelectorSettings);
    const commonSettings = useGradeSelectorSelector((state) => state.commonSettings);
    const multiEngineLabel = useCommonOptionalLabel("multipleEngineOptions");
    const ecoItems = getEcoItems(modelId, engines, commonSettings, disable);

    return multiEngineLabel && ecoItems.length > limit ? (
        <div className={clsx(styles["eco-item"], hasAdditionalIncentives && styles.border)}>
            <HybridLabel variant="multi" label={multiEngineLabel} />
        </div>
    ) : (
        <>
            {ecoItems.slice(0, limit).map((ecoItem, index) => (
                <div
                    className={clsx(
                        styles["eco-item"],
                        // Only show border when there are incentives, or if there are multiple ecoItems (except the last one)
                        ((ecoItems.length > 1 && index !== ecoItems.slice(0, limit).length - 1) ||
                            hasAdditionalIncentives) &&
                            styles.border,
                    )}
                    key={ecoItem.ecoTagLabel}
                >
                    <EcoItem
                        ecoItem={ecoItem}
                        commonSettings={commonSettings}
                        gradeSelectorSettings={gradeSelectorSettings}
                        disable={disable}
                    />
                </div>
            ))}
        </>
    );
};

export default memo(EcoItems);
